/** @jsx jsx */

import React, { useEffect } from "react";
import { jsx } from "@emotion/core";
import { Prompt } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";

// User defined components and hooks
import Button from "../../../components/button";
import {
  FormInput,
  FormDropdown,
  FormRadioButton,
  FormInputNumber,
  FormDatePicker
} from "../../../components/formHelpers";
import useIndustries from "../../../hooks/eligibility/useIndustries";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useSubIndustries from "../../../hooks/eligibility/useSubIndustries";
import useEligibilityMetaData from "../../../hooks/auth/useEligibilityMetaData";
import FormMultiInputTags, {
  MultiTags
} from "../../../components/formHelpers/FormMultiInputTags";
import useRedirect from "../../../helperHooks/useRedirect";

// Styles, Objects, constants.
import {
  CompanyDetailsFoldStyles,
  radioCapitalizeStyle
} from "./CompanyDetailsFoldStyles";
import { EligibilityRoutes } from "../../../Routes";
import { SaveEligibilityRequest } from "../../../store/eligibility/actions";
import {
  EligibilityDetailsState,
  CompanyDetailsState
} from "../../../store/eligibility/state";
import {
  CompanyDetailsFoldValidationSchema as validationSchema,
  CompanyDetailsFoldConstants as CONSTANTS,
  CompanyType
} from "./CompanyDetailsFoldConstants";
import {
  PropertyType,
  EligibilityViewConstants as ELIGIBILITY_CONSTANTS
} from "../EligibilityViewConstants";
import { unsavedChangeAlert } from "../../../Constants";
import { GstinValidityService } from "../../../services/gstinValidityService";

interface Props {
  eligibilityDetails: EligibilityDetailsState;
  submitting: boolean;
  saveEligibilityDetails: (data: SaveEligibilityRequest) => void;
}

const formatMultiTagsPayload = (list: MultiTags[]) =>
  list
    .filter(item => !item.removed && !item.error)
    .map(item => ({ gstin: item.value.toUpperCase() }));

const CompanyDetailsFold: React.FC<Props> = (props: Props) => {
  const {
    eligibilityDetails: { companyDetails },
    saveEligibilityDetails,
    submitting
  } = props;

  const redirect = useRedirect();

  const { disableEligibilityForms } = useLoanStatus();

  const { getIndustries, industriesData } = useIndustries();

  const { getSubIndustries, subIndustriesData } = useSubIndustries();

  const {
    eligibilityMetaData: { gstin }
  } = useEligibilityMetaData();

  //fetching industries when data is not available
  useEffect(() => {
    getIndustries();
  }, [getIndustries]);

  //initially fetch subIndustries only when industry is selected
  useEffect(() => {
    if (companyDetails?.industryType) {
      getSubIndustries(companyDetails.industryType);
    }
  }, [companyDetails, getIndustries, getSubIndustries]);

  const onSubmit = (
    data: CompanyDetailsState,
    formikHelpers: FormikHelpers<CompanyDetailsState>
  ) => {
    const {
      keyCustomers,
      keySuppliers,
      currentCustomer,
      currentSupplier,
      subIndustryType,
      subIndustryManual,
      isSubIndustryManual,
      ...foldData
    } = data; //to ignore customer, supplier data in "CompanyDetailsState"

    const manual = subIndustryType === CONSTANTS.labels.others;
    console.log(data);

    saveEligibilityDetails({
      data: {
        keyCustomers: formatMultiTagsPayload(keyCustomers),
        keySuppliers: formatMultiTagsPayload(keySuppliers),
        companyDetails: {
          ...foldData,
          isSubIndustryManual: manual,
          subIndustryType: manual ? subIndustryManual : subIndustryType,
          cinNumber:
            data.companyType === CompanyType["Private Limited"]
              ? data.cinNumber
              : "", // if company is "Private Limited", cinNumber is not required
          uanNumber:
            data.companyType !== CompanyType["Private Limited"]
              ? data.uanNumber
              : ""
        }
      },
      onSuccess: () => {
        //to reset initial state, so that, form is not "dirty"
        formikHelpers.resetForm();
        redirect(routes =>
          routes.eligibility.view(EligibilityRoutes.ApplicantFold)
        );
      }
    });
  };

  const renderForm = (
    <div css={CompanyDetailsFoldStyles}>
      <Formik
        initialValues={companyDetails}
        onSubmit={onSubmit}
        validationSchema={validationSchema(gstin)}
        validateOnChange
        validateOnBlur
      >
        {({ values, setFieldValue, dirty }) => (
          <Form>
            <Prompt when={dirty} message={unsavedChangeAlert} />
            <div className="grid-container">
              <FormRadioButton
                name="companyType"
                className="pb-1 mb-3 mb-md-0"
                customStyles={radioCapitalizeStyle}
                options={CompanyType}
                label={CONSTANTS.labels.companyType}
                disabled={disableEligibilityForms}
                mandatory
              />

              {values.companyType === CompanyType["Private Limited"] ? (
                <FormInput
                  name="cinNumber"
                  className="mb-3 mb-md-0"
                  label={CONSTANTS.labels.cinNumber}
                  disabled={disableEligibilityForms}
                  mandatory
                />
              ) : (
                <FormInput
                  name="uanNumber"
                  className="mb-3 mb-md-0"
                  label={CONSTANTS.labels.uanNumber}
                  disabled={disableEligibilityForms}
                  mandatory
                />
              )}
              <FormDatePicker
                name="dateOfIncorporation"
                className="mb-3 mb-md-0"
                format={CONSTANTS.labels.dateFormat}
                label={CONSTANTS.labels.dateOfIncorporation}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                name="unitAddress"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.unitAddress}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormRadioButton
                name="unitAddressType"
                className="mb-3 mb-md-0"
                options={PropertyType}
                label={CONSTANTS.labels.unitAddressType}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormDropdown
                className="mb-3 mb-md-0"
                placeholder={CONSTANTS.placeholders.industryType}
                name="industryType"
                options={industriesData.data}
                label={CONSTANTS.labels.industryType}
                loading={industriesData.loading}
                disable={disableEligibilityForms}
                mandatory
                onChange={value => {
                  //updating formik state
                  setFieldValue("industryType", value);
                  //refreshing subIndustries
                  getSubIndustries(value);
                  setFieldValue("subIndustryType", null);
                }}
              />
              <FormInput
                className="mb-3 mb-md-0"
                placeholder={CONSTANTS.placeholders.state}
                name="state"
                label={CONSTANTS.labels.state}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                placeholder={CONSTANTS.placeholders.city}
                name="city"
                label={CONSTANTS.labels.city}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                placeholder={CONSTANTS.placeholders.pincode}
                name="pincode"
                label={CONSTANTS.labels.pincode}
                disabled={disableEligibilityForms}
                mandatory
              />
              <div className="mb-3 mb-md-0">
                <FormDropdown
                  name="subIndustryType"
                  options={subIndustriesData.data}
                  loading={subIndustriesData.loading}
                  label={CONSTANTS.labels.subIndustryType}
                  placeholder={CONSTANTS.placeholders.subIndustryManual}
                  disable={disableEligibilityForms}
                  mandatory
                />
                {values.subIndustryType === CONSTANTS.labels.others && (
                  <FormInput
                    className="pt-3"
                    name="subIndustryManual"
                    disabled={disableEligibilityForms}
                    placeholder={CONSTANTS.placeholders.subIndustryManual}
                  />
                )}
              </div>
              <FormInputNumber
                className="mb-3 mb-md-0"
                name="employeesCount"
                label={CONSTANTS.labels.employeesCount}
                disabled={disableEligibilityForms}
              />
              <FormMultiInputTags
                name="currentCustomer"
                tooltipText={ELIGIBILITY_CONSTANTS.labels.keyCustomersTooltip}
                listName="keyCustomers"
                label={CONSTANTS.labels.keyCustomers}
                listItems={values.keyCustomers}
                onAdd={GstinValidityService.checkForValidity}
                disabled={disableEligibilityForms}
              />
              <FormMultiInputTags
                name="currentSupplier"
                tooltipText={ELIGIBILITY_CONSTANTS.labels.keySuppliersTooltip}
                listName="keySuppliers"
                label={CONSTANTS.labels.keySuppliers}
                listItems={values.keySuppliers}
                onAdd={GstinValidityService.checkForValidity}
                disabled={disableEligibilityForms}
              />
              <FormDropdown
                className="mb-3 mb-md-0"
                placeholder={CONSTANTS.placeholders.natureOfBusiness}
                name="natureOfBusiness"
                options={["Manufacturing", "Trading", "Services"]}
                label={CONSTANTS.labels.natureOfBusiness}
                loading={false}
                disable={disableEligibilityForms}
                mandatory
              />
              <FormInputNumber
                className="mb-3 mb-md-0"
                name="officeStability"
                label={CONSTANTS.labels.officeStability}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                name="reference1Name"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.reference1Name}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="reference1Contact"
                label={CONSTANTS.labels.reference1Contact}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                name="reference2Name"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.reference2Name}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="reference2Contact"
                label={CONSTANTS.labels.reference2Contact}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mt-4"
                name="officialEmail"
                label={CONSTANTS.labels.officialEmail}
                placeholder={CONSTANTS.placeholders.officialEmail}
                mandatory
              />
            </div>
            {!disableEligibilityForms && (
              <Button
                htmlType="submit"
                type="primary"
                containerDivClassName="justify-content-md-end mt-3"
                loading={submitting}
                disabled={submitting}
                className="mt-5 mb-5"
              >
                {CONSTANTS.buttons.next}
              </Button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );

  return renderForm;
};

export default CompanyDetailsFold;
